import { init, track, setUserId, identify, Identify, reset } from '@amplitude/analytics-browser';
import { LogLevel } from '@amplitude/analytics-browser/lib/esm/types';
import type { AnalyticsTransport, TimestampedAnalyticsEvent } from '@thinkalpha/ui-ioc/core/types/AnalyticsService';
import type { ConfigService } from '@thinkalpha/ui-ioc/core/types/ConfigService';
import type { Logger } from '@thinkalpha/ui-ioc/core/types/Logger';
import type { UserDetails } from '@thinkalpha/ui-types/types/user';
import { inject, injectable } from 'inversify';

// Hardcoded API key
const AMPLITUDE_API_KEY = '574abe25b20419fdbf423df3274ce71e';

@injectable()
export class AmplitudeWebTransport implements AnalyticsTransport {
    // eslint-disable-next-line no-restricted-syntax -- required pattern
    private initialized = false;
    // eslint-disable-next-line no-restricted-syntax -- required pattern
    private logger: Logger;

    constructor(
        @inject('Logger') baseLogger: Logger,
        @inject('ConfigService') private readonly configService: ConfigService,
    ) {
        this.logger = baseLogger.getSubLogger({ name: 'amplitude-transport' });
        this.initialize();
    }

    private initialize(): void {
        if (this.initialized) return;

        try {
            init(AMPLITUDE_API_KEY, {
                autocapture: false,
                logLevel: LogLevel.None,
            });

            this.#setNoUserIdentify();

            this.initialized = true;
            this.logger.info('Amplitude initialized successfully');
        } catch (error) {
            this.logger.error('Failed to initialize Amplitude', {
                error: error instanceof Error ? error : new Error(String(error)),
            });
        }
    }

    async sendEvents(events: TimestampedAnalyticsEvent[]): Promise<void> {
        if (!this.initialized) return;

        await Promise.all(
            events.map(async (event) => {
                try {
                    const { type, timestamp, ...properties } = event;
                    const result = await track(type, {
                        ...properties,
                        time: timestamp.getTime(),
                    }).promise;

                    if (result.code !== 200) {
                        throw new Error(`Amplitude track failed: ${result.message}`);
                    }

                    if (TRACE) {
                        this.logger.trace(`Sent event to Amplitude: ${type}`, { properties });
                    }
                } catch (error) {
                    this.logger.error(`Failed to send event to Amplitude: ${event.type}`, {
                        error: error instanceof Error ? error : new Error(String(error)),
                        event,
                    });
                }
            }),
        );
    }

    #setNoUserIdentify(): void {
        const identifyObj = new Identify();

        // Set the env at least
        identifyObj.set('env', this.configService.environmentName);

        identify(identifyObj);
    }

    setUser(user: UserDetails): void {
        if (!this.initialized) return;

        try {
            // Set the user ID in Amplitude
            setUserId(user.id);

            // Create an Identify object for user properties
            const identifyObj = new Identify();

            // Add all relevant user properties
            identifyObj.set('email', user.email);
            identifyObj.set('firstName', user.firstName);
            identifyObj.set('lastName', user.lastName);
            identifyObj.set('name', `${user.firstName} ${user.lastName}`);
            identifyObj.set('isSuperuser', user.isSuperuser);
            identifyObj.set('isDeveloper', user.isDeveloper);
            identifyObj.set('env', this.configService.environmentName);

            // Send the identify call to Amplitude
            identify(identifyObj);

            if (TRACE) {
                this.logger.debug(`Set Amplitude user: ${user.id}`);
            }
        } catch (error) {
            this.logger.error('Failed to set user in Amplitude', {
                error: error instanceof Error ? error : new Error(String(error)),
            });
        }
    }

    clearUser(): void {
        if (!this.initialized) return;

        try {
            // Reset the user and device ID
            reset();

            this.#setNoUserIdentify();

            if (TRACE) {
                this.logger.debug('Cleared user from Amplitude');
            }
        } catch (error) {
            this.logger.error('Failed to clear user in Amplitude', {
                error: error instanceof Error ? error : new Error(String(error)),
            });
        }
    }
}
